if (!customElements.get('tab-list')) {
  customElements.define(
    'tab-list',
    class TabList extends BaseElementMixin(HTMLUListElement) {
      connectedCallback() {
        super.connectedCallback();

        this.controls.forEach((button) => this.on(button, 'click', this.handleButtonClick.bind(this)));
      }

      get controls() {
        return this._controls = this._controls || Array.from(this.querySelectorAll('[aria-controls]'));
      }

      handleButtonClick(event) {
        event.preventDefault();

        this.controls.forEach((button) => {
          button.setAttribute('aria-expanded', 'false');

          const panel = document.getElementById(button.getAttribute('aria-controls'));
          panel?.removeAttribute('open');
        });

        const target = event.currentTarget;
        target.setAttribute('aria-expanded', 'true');

        const panel = document.getElementById(target.getAttribute('aria-controls'));
        panel?.setAttribute('open', '');
      }

      reset() {
        const firstControl = this.controls[0];
        firstControl.dispatchEvent(new Event('click'));
      }
    }, { extends: 'ul' }
  );
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends DrawerElement {
      constructor() {
        super();

        this.onPrepareBundledSectionsListener = this.onPrepareBundledSections.bind(this);
        this.onCartRefreshListener = this.onCartRefresh.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get shouldAppendToBody() {
        return false;
      }

      get recentlyViewed() {
        return this.querySelector('recently-viewed');
      }

      get tabList() {
        return this.querySelector('[is="tab-list"]');
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(document, 'cart:bundled-sections', this.onPrepareBundledSectionsListener);
        this.on(document, 'cart:refresh', this.onCartRefreshListener);
        if (this.recentlyViewed) {
          this.on(this.recentlyViewed, 'is-empty', this.onRecentlyViewedEmpty.bind(this));
        }
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }

      onRecentlyViewedEmpty() {
        this.recentlyViewed.innerHTML = `
        <div class="drawer__scrollable relative flex justify-center items-start grow shrink text-center">
          <div class="drawer__empty grid gap-5 md:gap-8">
            <p class="drawer__empty-text h2 heading leading-none tracking-tight">${theme.strings.recentlyViewedEmpty}</p>
          </div>
        </div>
        `;
      }

      async onCartRefresh(event) {
        const id = `MiniCart-${this.sectionId}`;
        if (document.getElementById(id) === null) return;

        this.refreshAbortController?.abort();
        this.refreshAbortController = new AbortController();

        let responseText;
        try {
          responseText = await (await fetch(`${theme.routes.root_url}?section_id=${this.sectionId}`, { signal: this.refreshAbortController.signal })).text();
        }
        catch (error) {
          if (error.name !== 'AbortError') console.error(error);
          return;
        }
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

        const source = parsedHTML.getElementById(id);
        if (source) document.getElementById(id).innerHTML = source.innerHTML;

        if (event.detail.open === true) {
          this.show();
        }
      }

      show(focusElement = null, animate = true) {
        super.show(focusElement, animate);

        if (this.tabList) {
          this.tabList.reset();

          if (this.open) {
            theme.a11y.trapFocus(this, this.focusElement);
          }
        }
      }
    }
  );
}

if (!customElements.get('cart-remove-button')) {
  customElements.define(
    'cart-remove-button',
    class CartRemoveButton extends BaseElementMixin(HTMLAnchorElement) {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'click', (event) => {
          event.preventDefault();

          const cartItems = this.closest('cart-items');
          cartItems.updateQuantity(this.getAttribute('data-index'), 0);
        });
      }
    }, { extends: 'a' }
  );
}

if (!customElements.get('cart-items')) {
  customElements.define(
    'cart-items',
    class CartItems extends BaseElement {

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'change', theme.utils.debounce(this.onChange.bind(this), 300));
        const cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        this.registerCleanup(cartUpdateUnsubscriber);
      }

      onChange(event) {
        this.validateQuantity(event);
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          return;
        }

        // Skip section update if source is 'assembly-service' or 'logistics-service' - these
        // managers already update sections via updateCartSections, so we don't need to update again.
        // This prevents double updates and avoids overwriting correct montage price with stale
        // sections when logistics removal and assembly updates race.
        const skipSectionUpdate = event.source === 'assembly-service' || event.source === 'logistics-service';

        if (typeof event.cart.sections === 'undefined') return;
        
        // Define these variables in outer scope so they're available for focus handling later
        let miniCart = null;
        let mainCart = null;
        
        if (!skipSectionUpdate) {
          const sectionToRender = new DOMParser().parseFromString(event.cart.sections[this.sectionId], 'text/html');
          // Prevent re-executing module scripts embedded in cart sections on morph
          sectionToRender.querySelectorAll('script').forEach((script) => script.remove());
          const miniCartId = `MiniCart-${this.sectionId}`;
          const mainCartId = `MainCart-${this.sectionId}`;
          
          miniCart = document.getElementById(miniCartId);
          if (miniCart) {
            const updatedElement = sectionToRender.getElementById(miniCartId);
            if (updatedElement) {
              if (event.source === 'cart-discount') {
                const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
                if (cartDiscount) {
                  cartDiscount.hidden = false
                  cartDiscount.setAttribute('open', 'immediate');
                  cartDiscount.setAttribute('active', '');
                }
              }
              
              // Filter out service products before updating
              import('./cart-service-filter.js').then(({ filterServiceProductsFromCart }) => {
                filterServiceProductsFromCart(updatedElement);
                miniCart.innerHTML = updatedElement.innerHTML;
              }).catch(() => {
                miniCart.innerHTML = updatedElement.innerHTML;
              });
            }
          }

          mainCart = document.getElementById(mainCartId);
          if (mainCart) {
            const updatedElement = sectionToRender.getElementById(mainCartId);
            if (updatedElement) {
              if (event.source === 'cart-discount') {
                const cartDiscount = updatedElement.querySelector(`#CartDiscount-${this.sectionId}`);
                if (cartDiscount) {
                  cartDiscount.hidden = false
                  cartDiscount.setAttribute('open', '');
                  cartDiscount.setAttribute('aria-expanded', 'true');
                }
              }
              
              const scrollTop = window.scrollY;
              
              // Filter out service products before updating
              import('./cart-service-filter.js').then(({ filterServiceProductsFromCart }) => {
                filterServiceProductsFromCart(updatedElement);
                
                // Ensure assembly management timeout persists after DOM replacement
                // Store timeout ID before replacing innerHTML
                if (this.assemblyManageTimeout && window._assemblyManageTimeouts) {
                  const sectionId = this.sectionId;
                  window._assemblyManageTimeouts.set(sectionId, this.assemblyManageTimeout);
                }
                
                mainCart.innerHTML = updatedElement.innerHTML;

                if (event.source === 'cart-discount') {
                  requestIdleCallback(() => {
                    window.scrollTo({
                      top: scrollTop,
                      behavior: 'instant'
                    });
                  });
                }
              }).catch(() => {
                mainCart.innerHTML = updatedElement.innerHTML;
                if (event.source === 'cart-discount') {
                  requestIdleCallback(() => {
                    window.scrollTo({
                      top: scrollTop,
                      behavior: 'instant'
                    });
                  });
                }
              });
            } else {
              mainCart.closest('.cart').classList.add('is-empty');
              mainCart.remove();
            }
          }
        } else {
          // Still need to get references for focus handling even if we skip section update
          miniCart = document.getElementById(`MiniCart-${this.sectionId}`);
          mainCart = document.getElementById(`MainCart-${this.sectionId}`);
        }

        const lineItem = document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          const focusTarget = lineItem.querySelector(`[name="${event.name}"]`);
          if (focusTarget && (mainCart || miniCart)) {
            theme.a11y.trapFocus(mainCart || miniCart, focusTarget);
          }
        }
        else if (event.cart.item_count === 0) {
          const emptyState = document.querySelector('.empty-state');
          const emptyStateLink = emptyState?.querySelector('.empty-state__link');
          const miniCartLink = miniCart?.querySelector('a');
          
          if (miniCartLink) {
            theme.a11y.trapFocus(miniCart, miniCartLink);
          } else if (emptyState && emptyStateLink) {
            theme.a11y.trapFocus(emptyState, emptyStateLink);
          }
        }
        else {
          const miniCartTitle = miniCart?.querySelector('.horizontal-product__title');
          const mainCartTitle = mainCart?.querySelector('.cart__item-title');
          
          if (miniCart && miniCartTitle) {
            theme.a11y.trapFocus(miniCart, miniCartTitle);
          } else if (mainCart && mainCartTitle) {
            theme.a11y.trapFocus(mainCart, mainCartTitle);
          }
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: event.cart
          }
        }));
        
        // Check if assembly product needs to be managed after cart update
        // Skip if update came from assembly service itself to prevent loops
        if (event.source !== 'assembly-service') {
          // Store section ID and section update status before timeout
          const sectionId = this.sectionId;
          const shouldUpdateSections = !skipSectionUpdate;
          
          // Always check after cart updates - don't try to detect removals specifically
          // The manageAssemblyProduct function will determine if assembly product should be removed
          // Use a delay to ensure cart state is updated on server before checking
          // Longer delay for cart page since section updates replace DOM elements
          const delay = shouldUpdateSections ? 300 : 200;
          
          console.log('[Cart Items] 🛒 CART UPDATE - Managing assembly product:', { 
            source: event.source,
            itemCount: event.cart?.item_count,
            itemsLength: event.cart?.items?.length,
            delay,
            sectionId,
            skipSectionUpdate,
            cartItems: event.cart?.items?.map(item => ({
              variantId: item.variant_id,
              quantity: item.quantity,
              hasAssembly: item.properties?.['_assembly-service'] === '1'
            }))
          });
          
          clearTimeout(this.assemblyManageTimeout);
          
          // Store timeout reference on window or document to persist even if element is replaced
          // This ensures the timeout fires even if the cart-items element is replaced during section update
          const timeoutId = setTimeout(() => {
            // Clean up from window map
            if (window._assemblyManageTimeouts) {
              window._assemblyManageTimeouts.delete(sectionId);
            }
            
            console.log('[Cart Items] ⏰ Timer fired - calling manageAssemblyProduct...');
            import('./assembly-service-manager.js').then(async ({ manageAssemblyProduct, updateCartSections }) => {
              // Re-collect sections to update in case DOM changed
              const sectionsToUpdate = new Set();
              
              // Try to find current cart-items element (might have been replaced)
              const currentCartItems = document.querySelector(`cart-items[data-section-id="${sectionId}"]`);
              if (currentCartItems) {
                sectionsToUpdate.add(sectionId);
                // Clear any existing timeout on the new element
                if (currentCartItems.assemblyManageTimeout) {
                  clearTimeout(currentCartItems.assemblyManageTimeout);
                }
              } else {
                // Fallback: collect from all cart-items elements
                document.querySelectorAll('cart-items').forEach((element) => {
                  const sid = element.getAttribute('data-section-id');
                  if (sid) {
                    sectionsToUpdate.add(sid);
                    // Clear any existing timeout on the element
                    if (element.assemblyManageTimeout) {
                      clearTimeout(element.assemblyManageTimeout);
                    }
                  }
                });
              }
              
              // Also collect from main-cart and cart-drawer elements
              document.querySelectorAll('main-cart').forEach((element) => {
                const sid = element.getAttribute('data-section-id');
                if (sid) sectionsToUpdate.add(sid);
              });
              document.querySelectorAll('cart-drawer').forEach((element) => {
                const sid = element.getAttribute('data-section-id');
                if (sid) sectionsToUpdate.add(sid);
              });
              
              console.log('[Cart Items] ✅ Import successful - calling manageAssemblyProduct with sections:', Array.from(sectionsToUpdate));
              
              // Fetch fresh cart so managers see post-removal state (not the stale event snapshot)
              let cartForAssembly = event.cart;
              try {
                const cartUrl = theme.routes?.cart_url || '/cart';
                const cartJsUrl = cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
                const cartResponse = await fetch(cartJsUrl, theme.utils.fetchConfig('json', 'GET'));
                if (cartResponse.ok) {
                  cartForAssembly = await cartResponse.json();
                }
              } catch (fetchError) {
                console.warn('[Cart Items] Could not fetch fresh cart for assembly check:', fetchError);
              }

              manageAssemblyProduct(cartForAssembly, sectionsToUpdate.size > 0 ? sectionsToUpdate : null, false)
                .then(async (updatedSections) => {
                  // Update cart sections with returned sections to refresh summary
                  if (updatedSections && Object.keys(updatedSections).length > 0) {
                    updateCartSections(updatedSections, sectionsToUpdate);
                    console.log('[Cart Items] ✅ Cart sections updated after assembly product management');
                  }
                })
                .catch((error) => {
                  console.error('[Cart Items] ❌ Error managing assembly product:', error);
                });
            }).catch((error) => {
              console.warn('[Cart Items] ⚠️ Failed to load assembly service manager:', error);
            });
          }, delay);
          
          // Store timeout ID on the element
          this.assemblyManageTimeout = timeoutId;
          
          // Also store on window as backup in case element is replaced
          if (!window._assemblyManageTimeouts) {
            window._assemblyManageTimeouts = new Map();
          }
          // Clear any existing timeout for this section
          if (window._assemblyManageTimeouts.has(sectionId)) {
            clearTimeout(window._assemblyManageTimeouts.get(sectionId));
          }
          window._assemblyManageTimeouts.set(sectionId, timeoutId);
          
        } else {
          console.log('[Cart Items] ⏭️ Skipping assembly product management - source is assembly-service');
        }
      }

      onCartError(errors, target) {
        if (target) {
          // this.updateQuantity(target.getAttribute('data-index'), target.defaultValue, document.activeElement.getAttribute('name'), target);
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        }
        else {
          window.location.href = theme.routes.cart_url;
        }
      }

      updateQuantity(line, quantity, name, target) {
        this.enableLoading(line);

        this.updateAbortController?.abort();
        this.updateAbortController = new AbortController();

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const body = JSON.stringify({
          id: line,
          quantity,
          sections: sectionsToBundle
        });

        fetch(theme.routes.cart_change_url, { ...theme.utils.fetchConfig(), ...{ body }, signal: this.updateAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cart: parsedState, target, line, name });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          });
      }

      enableLoading(line) {
        this.classList.add('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = false;
      }

      disableLoading(line) {
        this.classList.remove('pointer-events-none');

        const loader = document.getElementById(`Loader-${this.sectionId}-${line}`);
        if (loader) loader.hidden = true;
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        }
        else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'change', theme.utils.debounce(this.onChange.bind(this), 300));
      }

      onChange(event) {
        const body = JSON.stringify({ note: event.target.value });
        // Fire-and-forget attribute update — the response is ignored, so there is
        // no stale-response risk and nothing to abort.
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('main-cart')) {
  customElements.define(
    'main-cart',
    class MainCart extends BaseElement {
      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      connectedCallback() {
        super.connectedCallback();

        this.on(document, 'cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('country-province')) {
  customElements.define(
    'country-province',
    class CountryProvince extends BaseElement {
      connectedCallback() {
        super.connectedCallback();

        this.provinceElement = this.querySelector('[name="address[province]"]');
        this.countryElement = this.querySelector('[name="address[country]"]');
        this.on(this.countryElement, 'change', this.handleCountryChange.bind(this));

        if (this.getAttribute('country') !== '') {
          this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('data-country')));
          this.countryElement.dispatchEvent(new Event('change'));
        }
        else {
          this.handleCountryChange();
        }
      }

      handleCountryChange() {
        const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute('data-provinces'));
        this.provinceElement.parentElement.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          return;
        }

        this.provinceElement.innerHTML = '';

        provinces.forEach((data) => {
          const selected = data[1] === this.getAttribute('data-province');
          this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
        });
      }
    }
  );
}

if (!customElements.get('shipping-calculator')) {
  customElements.define(
    'shipping-calculator',
    class ShippingCalculator extends BaseElementMixin(HTMLFormElement) {
      constructor() {
        super();

        this.onSubmitHandler = this.onSubmit.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();

        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.on(this.submitButton, 'click', this.onSubmitHandler);
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.ratesAbortController?.abort();
      }

      onSubmit(event) {
        event.preventDefault();

        this.ratesAbortController?.abort();
        this.ratesAbortController = new AbortController();

        const zip = this.querySelector('[name="address[zip]"]').value,
          country = this.querySelector('[name="address[country]"]').value,
          province = this.querySelector('[name="address[province]"]').value;

        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          shipping_address: { zip, country, province }
        });
        let sectionUrl = `${theme.routes.cart_url}/shipping_rates.json`;

        // remove double `/` in case shop might have /en or language in URL
        sectionUrl = sectionUrl.replace('//', '/');

        fetch(sectionUrl, { ...theme.utils.fetchConfig('javascript'), ...{ body }, signal: this.ratesAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.shipping_rates) {
              this.formatShippingRates(parsedState.shipping_rates);
            }
            else {
              this.formatError(parsedState);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            this.resultsElement.hidden = false;
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      formatError(errors) {
        const shippingRatesList = Object.keys(errors).map((errorKey) => {
          return `<li>${errors[errorKey]}</li>`;
        });
        this.resultsElement.classList.remove('alert--success');
        this.resultsElement.classList.add('alert--error');
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${theme.shippingCalculatorStrings.error}</p>
          <ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>
        `;
      }

      formatShippingRates(shippingRates) {
        const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
          return `<li>${presentment_name}: ${currency} ${price}</li>`;
        });
        if (shippingRates.length) {
          this.resultsElement.classList.remove('alert--error');
          this.resultsElement.classList.add('alert--success');
        }
        else {
          this.resultsElement.classList.remove('alert--success');
          this.resultsElement.classList.add('alert--error');
        }
        this.resultsElement.lastElementChild.innerHTML = `
          <p>${shippingRates.length === 0 ? theme.shippingCalculatorStrings.notFound : shippingRates.length === 1 ? theme.shippingCalculatorStrings.oneResult : theme.shippingCalculatorStrings.multipleResults}</p>
          ${shippingRatesList === '' ? '' : `<ul class="list-disc grid gap-2" role="list">${shippingRatesList.join('')}</ul>`}
        `;
      }
    }, { extends: 'form' }
  );
}

/** Pre-discount cart merchandise below this (cents) → L4M1A shows “spend __AMOUNT__ more” for 30% tier */
const L4M1A_THRESHOLD_CENTS = 2000000;

/**
 * Sum of each line’s merchandise before line-level discounts: prefer Shopify’s pre-discount line total,
 * else unit price before sale × quantity. (Does not use cart items_subtotal_price / post-discount totals.)
 * @see https://shopify.dev/docs/api/ajax/reference/cart#json-of-a-cart-item
 */
function getL4M1ACartPreDiscountMerchandiseCents(parsedState) {
  const items = parsedState?.items;
  if (!Array.isArray(items)) return 0;
  return items.reduce((sum, item) => {
    const qty = Math.max(1, Number(item.quantity) || 1);

    const originalLine = Number(item.original_line_price);
    if (Number.isFinite(originalLine) && originalLine >= 0) {
      return sum + originalLine;
    }

    const originalUnit = Number(item.original_price);
    if (Number.isFinite(originalUnit) && originalUnit >= 0) {
      return sum + originalUnit * qty;
    }

    // Rare: some responses expose compare-at on the line
    const compareUnit = Number(item.compare_at_price);
    if (Number.isFinite(compareUnit) && compareUnit > 0) {
      return sum + compareUnit * qty;
    }

    // Last resort only (may match current selling price if Shopify omits original_* )
    const unit = Number(item.price);
    if (Number.isFinite(unit) && unit >= 0) {
      return sum + unit * qty;
    }

    return sum;
  }, 0);
}

function formatMoneyCentsForDiscount(cents) {
  if (typeof theme !== 'undefined' && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
    const format = theme.settings?.currencyCodeEnabled
      ? theme.settings?.moneyWithCurrencyFormat
      : theme.settings?.moneyFormat;
    return theme.Currency.formatMoney(cents, format || '');
  }
  return (Number(cents) / 100).toFixed(2);
}

/**
 * Injects formatted shortfall into theme.discountStrings.l4m1aAlreadyApplied.
 * `merchandiseCents` = sum of pre-discount line amounts from getL4M1ACartPreDiscountMerchandiseCents.
 * Supports: __AMOUNT__, [amount], {{ amount }}, fullwidth brackets (after NFKC).
 */
function buildL4M1ADiscountMessage(merchandiseCents) {
  let template = typeof theme !== 'undefined' && theme.discountStrings?.l4m1aAlreadyApplied;
  if (!template) return theme?.discountStrings?.error || '';
  const shortfall = Math.max(0, L4M1A_THRESHOLD_CENTS - (Number(merchandiseCents) || 0));
  let amountFormatted;
  try {
    amountFormatted = formatMoneyCentsForDiscount(shortfall);
  } catch (e) {
    amountFormatted = (shortfall / 100).toFixed(2);
  }
  template = String(template);
  try {
    template = template.normalize('NFKC');
  } catch (e) {
    /* ignore */
  }
  return template
    .replace(/__AMOUNT__/gi, amountFormatted)
    .replace(/\{\{\s*amount\s*\}\}/gi, amountFormatted)
    .replace(/\[amount\]/gi, amountFormatted)
    .replace(/%amount%/gi, amountFormatted)
    .replace(/[\[［]\s*amount\s*[\]］]/gi, amountFormatted);
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends BaseElementMixin(HTMLFormElement) {
      constructor() {
        super();

        this.onApplyDiscount = this.applyDiscount.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      connectedCallback() {
        super.connectedCallback();

        this.submitButton = this.querySelector('[type="submit"]');
        this.resultsElement = this.lastElementChild;

        this.on(this.submitButton, 'click', this.onApplyDiscount);
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        this.discountAbortController?.abort();
      }

      applyDiscount(event) {
        event.preventDefault();

        const discountCode = this.querySelector('[name="discount"]');
        if (!(discountCode instanceof HTMLInputElement) || typeof this.getAttribute('data-section-id') !== 'string') return;

        this.discountAbortController?.abort();
        this.discountAbortController = new AbortController();

        const discountCodeValue = discountCode.value.trim();
        if (discountCodeValue === '') return;

        const existingDiscounts = this.existingDiscounts();
        if (existingDiscounts.includes(discountCodeValue)) return;

        this.setDiscountError('');
        this.submitButton.setAttribute('aria-busy', 'true');

        const body = JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.discountAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            const invalidAppliedDiscount = parsedState.discount_codes.find((discount) => {
              return discount.code === discountCodeValue && discount.applicable === false;
            });
            if (invalidAppliedDiscount) {
              discountCode.value = '';

              // Special handling for B2B code L4M1A:
              // If pre-discount merchandise (Σ line: original_line_price or original_price × qty) is below threshold, show shortfall.
              if (discountCodeValue.trim().toUpperCase() === 'L4M1A' && Array.isArray(parsedState.items) && parsedState.items.length > 0) {
                const merchandiseCents = getL4M1ACartPreDiscountMerchandiseCents(parsedState);

                if (merchandiseCents < L4M1A_THRESHOLD_CENTS) {
                  this.setDiscountError(buildL4M1ADiscountMessage(merchandiseCents));
                  return;
                }
              }

              this.setDiscountError(theme.discountStrings.error);
              return;
            }

            const newHtml = parsedState.sections[this.sectionId];
            const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
            const section = parsedHtml.getElementById(`shopify-section-${this.sectionId}`);
            if (section) {
              const discountCodes = section?.querySelectorAll('button[is="discount-remove"]') || [];
              const codes = Array.from(discountCodes)
                .map((element) => (element instanceof HTMLButtonElement ? element.getAttribute('data-discount') : null))
                .filter(Boolean);

              if (
                codes.length === existingDiscounts.length &&
                codes.every((code) => existingDiscounts.includes(code)) &&
                parsedState.discount_codes.find((discount) => {
                  return discount.code === discountCodeValue && discount.applicable === true;
                })
              ) {
                discountCode.value = '';
                this.setDiscountError(theme.discountStrings.shippingError);
                return;
              }
            }

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            this.submitButton.removeAttribute('aria-busy');
          });
      }

      removeDiscount(event) {
        if ((event instanceof KeyboardEvent && event.key !== 'Enter') || !(event instanceof MouseEvent)) {
          return;
        }

        const discountCode = event.target.getAttribute('data-discount');
        if (!discountCode) return;

        const existingDiscounts = this.existingDiscounts();
        const index = existingDiscounts.indexOf(discountCode);
        if (index === -1) return;

        existingDiscounts.splice(index, 1);

        this.discountAbortController?.abort();
        this.discountAbortController = new AbortController();

        this.setDiscountError('');
        event.target.setAttribute('loading', '');

        const body = JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.sectionId]
        });
        
        fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig('json'), ...{ body }, signal: this.discountAbortController.signal })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cart: parsedState });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') console.error(error);
          })
          .finally(() => {
            event.target.removeAttribute('loading');
          });
      }

      existingDiscounts() {
        const discountCodes = [];
        const discountPills = this.querySelectorAll('button[is="discount-remove"]');
        for (const pill of discountPills) {
          if (pill.hasAttribute('data-discount')) {
            discountCodes.push(pill.getAttribute('data-discount'));
          }
        }
        return discountCodes;
      }

      setDiscountError(error) {
        this.resultsElement.lastElementChild.textContent = error;
        this.resultsElement.hidden = error.length === 0;
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('discount-remove')) {
  customElements.define(
    'discount-remove',
    class DiscountRemove extends MagnetButton {
      connectedCallback() {
        super.connectedCallback();

        this.on(this, 'click', this.onClick);
      }

      onClick(event) {
        const form = this.closest('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');

        if (form) {
          event.preventDefault();
          form.removeDiscount(event);
        }
      }
    }, { extends: 'button' }
  );
}
